/**
 * <bg-nebula> — drifting volumetric clouds for the cosmic backdrop.
 *
 * Soft radial-gradient blobs that drift slowly across the viewport.
 * Brand-aligned palette: gold-soft, deep violet, brass amber. Each
 * soundtrack stem drives a unique property:
 *
 *   Lead vocals    → ALPHA cap (cloud brightness)
 *   Backing vocals → DAMPING (drift smoothness — slower decay = silkier motion)
 *   Drums          → SPAWN cadence (beat = burst)
 *   Bass           → cloud RADIUS (size)
 *   Guitar         → edge SOFTNESS (blur)
 *   Percussion     → inner SPARKLE count
 *   Strings        → DRIFT VELOCITY (push speed — pads = wide motion)
 *   Synth          → BLOOM radius
 */

import { readAllStems, mix, clamp } from "./stem-levels.ts";
import { getCanvasDpr, getFrameBudgetMs, getParticleScale, shouldRender } from "./perf-budget.ts";

const MAX_CLOUDS = Math.max(2, Math.floor(14 * getParticleScale()));
const SPAWN_COOLDOWN_MS = 1800;  // 900 → 1800 (halve spawn rate)
const FRAME_BUDGET_MS = getFrameBudgetMs("atmosphere");

type Cloud = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  hue: number;
  bornAt: number;
  lifeMs: number;
  sparkles: ReadonlyArray<{ dx: number; dy: number; phase: number }>;
};

const rand = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

class BgNebula extends HTMLElement {
  #canvas: HTMLCanvasElement | null = null;
  #ctx: CanvasRenderingContext2D | null = null;
  #rafId: number | null = null;
  #lastFrame: number = 0;
  #lastSpawn: number = 0;
  #clouds: Cloud[] = [];
  #w: number = 0;
  #h: number = 0;
  #resizeObserver: ResizeObserver | null = null;
  #visibilityHandler: (() => void) | null = null;

  connectedCallback(): void {
    // Low-tier devices skip the nebula entirely — it's the heaviest
    // fill-rate layer (large soft clouds with mix-blend-mode: screen).
    if (!shouldRender("nebula")) return;
    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;display:block;";
    this.style.position = this.style.position || "fixed";
    this.style.inset = "0";
    this.style.pointerEvents = "none";
    this.style.zIndex = "0";
    this.style.mixBlendMode = "screen";
    this.appendChild(canvas);
    this.#canvas = canvas;
    this.#ctx = canvas.getContext("2d");
    if (this.#ctx === null) return;
    this.#resize();
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);
    this.#visibilityHandler = (): void => {
      if (document.hidden) this.#stop();
      else this.#start();
    };
    document.addEventListener("visibilitychange", this.#visibilityHandler);
    this.#start();
  }

  disconnectedCallback(): void {
    this.#stop();
    this.#resizeObserver?.disconnect();
    if (this.#visibilityHandler !== null) {
      document.removeEventListener("visibilitychange", this.#visibilityHandler);
      this.#visibilityHandler = null;
    }
    this.#clouds = [];
  }

  #resize(): void {
    if (this.#canvas === null || this.#ctx === null) return;
    const dpr = getCanvasDpr();
    this.#w = this.clientWidth || window.innerWidth;
    this.#h = this.clientHeight || window.innerHeight;
    this.#canvas.width = Math.max(1, Math.floor(this.#w * dpr));
    this.#canvas.height = Math.max(1, Math.floor(this.#h * dpr));
    this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  #spawn(now: number, bass: number, strings: number, backing: number, percussion: number): void {
    if (this.#clouds.length >= MAX_CLOUDS) this.#clouds.shift();
    const radius = mix(120, 380, bass) + rand(-40, 40);
    // Brand palette hues: 50 (warm gold), 75 (cream), 295 (violet), 17 (amber)
    const hueBase = [50, 75, 295, 17][Math.floor(Math.random() * 4)] ?? 50;
    const hue = hueBase + (backing - 0.5) * 60;
    const lifeMs = mix(8000, 22000, strings) + rand(-1200, 1200);
    // Sparkle count from percussion
    const spcount = Math.floor(mix(0, 10, percussion));
    const sparkles = Array.from({ length: spcount }, () => ({
      dx: rand(-radius * 0.6, radius * 0.6),
      dy: rand(-radius * 0.6, radius * 0.6),
      phase: Math.random() * Math.PI * 2,
    }));
    this.#clouds.push({
      x: rand(0, this.#w),
      y: rand(0, this.#h),
      vx: rand(-6, 6),
      vy: rand(-3, 3),
      radius,
      hue,
      bornAt: now,
      lifeMs,
      sparkles,
    });
  }

  #start(): void {
    if (this.#rafId !== null) return;
    this.#lastFrame = performance.now();
    const tick = (now: number): void => {
      const dt = now - this.#lastFrame;
      if (dt >= FRAME_BUDGET_MS) {
        this.#lastFrame = now;
        this.#step(Math.min(50, dt), now);
        this.#render(now);
      }
      this.#rafId = requestAnimationFrame(tick);
    };
    this.#rafId = requestAnimationFrame(tick);
  }

  #stop(): void {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #step(dt: number, now: number): void {
    const s = readAllStems();
    // Drums-driven spawn cadence (rhythm = burst)
    const cooldown = mix(2400, SPAWN_COOLDOWN_MS, s.drums);
    if (now - this.#lastSpawn >= cooldown) {
      this.#spawn(now, s.bass, s.strings, s["backing-vocals"], s.percussion);
      this.#lastSpawn = now;
    }
    // BACKING VOCALS → damping (smoother decay = silkier drift)
    const damp = mix(0.985, 0.998, s["backing-vocals"]);
    // STRINGS → drift velocity push (sustained pads = sweep)
    const pushMul = mix(0.7, 2.4, s.strings);
    const dts = dt / 1000;
    for (let i = this.#clouds.length - 1; i >= 0; i -= 1) {
      const c = this.#clouds[i];
      if (!c) continue;
      c.x += c.vx * pushMul * dts;
      c.y += c.vy * pushMul * dts;
      c.vx *= damp;
      c.vy *= damp;
      const age = now - c.bornAt;
      if (age > c.lifeMs) {
        this.#clouds.splice(i, 1);
      }
    }
  }

  #render(now: number): void {
    if (this.#ctx === null) return;
    const ctx = this.#ctx;
    const s = readAllStems();
    ctx.clearRect(0, 0, this.#w, this.#h);
    ctx.globalCompositeOperation = "lighter";

    // Guitar drives edge softness (blur multiplier); synth drives bloom
    const blurMul = mix(1.4, 2.6, s.guitar);
    const bloomMul = mix(1.0, 1.8, s.synth);

    for (const c of this.#clouds) {
      const age = now - c.bornAt;
      const lifeT = clamp(age / c.lifeMs, 0, 1);
      let envelope = 1;
      if (lifeT < 0.15) envelope = lifeT / 0.15;
      else if (lifeT > 0.7) envelope = 1 - (lifeT - 0.7) / 0.3;

      // Lead vocals drive overall alpha cap — illustrious at peak
      const alpha = envelope * mix(0.10, 0.75, s["lead-vocals"]);

      // Outer bloom
      const bloomR = c.radius * bloomMul;
      const bloom = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, bloomR);
      bloom.addColorStop(0, `hsla(${c.hue}, 80%, 65%, ${alpha * 0.9})`);
      bloom.addColorStop(0.4, `hsla(${c.hue}, 75%, 55%, ${alpha * 0.45})`);
      bloom.addColorStop(1, `hsla(${c.hue}, 70%, 45%, 0)`);
      ctx.fillStyle = bloom;
      ctx.beginPath();
      ctx.arc(c.x, c.y, bloomR, 0, Math.PI * 2);
      ctx.fill();

      // Inner core — soft, blurred via shadowBlur
      ctx.shadowColor = `hsla(${c.hue}, 90%, 70%, ${alpha})`;
      ctx.shadowBlur = c.radius * 0.25 * blurMul;
      ctx.fillStyle = `hsla(${c.hue}, 85%, 70%, ${alpha * 0.7})`;
      ctx.beginPath();
      ctx.arc(c.x, c.y, c.radius * 0.25, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Sparkles inside the cloud (percussion-driven count)
      for (const sp of c.sparkles) {
        const twink = (Math.sin(sp.phase + now * 0.006) + 1) / 2;
        const sparkAlpha = alpha * twink * 1.4;
        if (sparkAlpha < 0.02) continue;
        ctx.fillStyle = `hsla(${c.hue}, 90%, 92%, ${sparkAlpha})`;
        ctx.beginPath();
        ctx.arc(c.x + sp.dx, c.y + sp.dy, 1.3, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalCompositeOperation = "source-over";
  }
}

export const registerBgNebula = (): void => {
  if (customElements.get("bg-nebula") !== undefined) return;
  customElements.define("bg-nebula", BgNebula);
};
